import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import NotificationDropdown from '../notifications/NotificationDropdown';
import UserDropdown from '../UserDropdown/UserDropdown';
import { 
  CreditCardIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  console.log('Layout: Rendering layout component');
  console.log('Layout: Children received:', !!children);
  const location = useLocation();
  const { userProfile, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // const unreadCount = useUnreadMessages(); // Temporarily disabled for debugging
  const unreadCount = 0;
  
  // Check if user is authenticated
  const isAuthenticated = currentUser && userProfile;
  // Check if user is an employer based on profile type
  const isEmployer = userProfile && userProfile.userType === 'employer';

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getLinkClasses = (path: string) =>
    `transition-colors ${
      isActivePath(path)
        ? 'text-indigo-600 font-semibold'
        : 'text-gray-600 hover:text-gray-900'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-xl font-bold text-gray-900">Workazon</h1>
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className={getLinkClasses('/dashboard')}>
                    Dashboard
                  </Link>
                  {isEmployer ? (
                    <>
                      <Link to="/employer/job-postings" className={getLinkClasses('/employer/job-postings')}>
                        My Postings
                      </Link>
                      <Link to="/employer/job-posting" className={getLinkClasses('/employer/job-posting')}>
                        Post Job
                      </Link>
                      <Link to="/employer/applications" className={getLinkClasses('/employer/applications')}>
                        Applications
                      </Link>
                      <Link to="/employer/analytics" className={getLinkClasses('/employer/analytics')}>
                        Analytics
                      </Link>
                      <Link to="/messages" className={`${getLinkClasses('/messages')} relative`}>
                        Messages
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-4 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/jobs" className={getLinkClasses('/jobs')}>
                        Jobs
                      </Link>
                      <Link to="/courses" className={getLinkClasses('/courses')}>
                        Courses
                      </Link>
                      {/* <Link to="/rankings" className={getLinkClasses('/rankings')}>
                        Rankings
                      </Link> */}
                      <Link to="/messages" className={`${getLinkClasses('/messages')} relative`}>
                        Messages
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-4 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    </>
                  )}
                  <Link 
                    to="/subscription" 
                    className={`flex items-center ${getLinkClasses('/subscription')}`}
                  >                
                    Subscription
                  </Link>
                  <Link to="/support" className={getLinkClasses('/support')}>
                    Support
                  </Link>

                  {/* Notifications */}
                  <NotificationDropdown />

                  {/* User Dropdown */}
                  <div className="flex items-center ml-6 pl-6 border-l border-gray-200">
                    <UserDropdown />
                  </div>
                </>
              ) : (
                // Anonymous user navigation
                <>
                  <Link to="/jobs" className={getLinkClasses('/jobs')}>
                    Jobs
                  </Link>
                  {/* <Link to="/talent" className={getLinkClasses('/talent')}>
                    Talent
                  </Link> */}
                  <div className="flex items-center space-x-4">
                    <Link 
                      to="/login" 
                      className="text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Log In
                    </Link>
                    <Link 
                      to="/register" 
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors font-medium"
                    >
                      Sign Up
                    </Link>
                  </div>
                </>
              )}
            </nav>

            {/* Mobile menu button & user menu */}
            <div className="md:hidden flex items-center space-x-4">
              {isAuthenticated && <NotificationDropdown />}
              {isAuthenticated && <UserDropdown className="mr-2" />}
              {!isAuthenticated && (
                <div className="flex items-center space-x-2">
                  <Link 
                    to="/login" 
                    className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                  >
                    Log In
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              <nav className="flex flex-col space-y-4">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className={`${getLinkClasses('/dashboard')} px-2 py-1`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    {isEmployer ? (
                      <>
                        <Link 
                          to="/employer/job-postings" 
                          className={`${getLinkClasses('/employer/job-postings')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          My Postings
                        </Link>
                        <Link 
                          to="/employer/job-posting" 
                          className={`${getLinkClasses('/employer/job-posting')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Post Job
                        </Link>
                        <Link 
                          to="/employer/applications" 
                          className={`${getLinkClasses('/employer/applications')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Applications
                        </Link>
                        <Link 
                          to="/employer/analytics" 
                          className={`${getLinkClasses('/employer/analytics')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Analytics
                        </Link>
                        <Link 
                          to="/messages" 
                          className={`${getLinkClasses('/messages')} px-2 py-1 relative`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Messages
                          {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link 
                          to="/jobs" 
                          className={`${getLinkClasses('/jobs')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Jobs
                        </Link>
                        <Link 
                          to="/courses" 
                          className={`${getLinkClasses('/courses')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Courses
                        </Link>
                        {/* <Link 
                          to="/rankings" 
                          className={`${getLinkClasses('/rankings')} px-2 py-1`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Rankings
                        </Link> */}
                        <Link 
                          to="/messages" 
                          className={`${getLinkClasses('/messages')} px-2 py-1 relative`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Messages
                          {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </Link>
                      </>
                    )}
                    <Link 
                      to="/subscription" 
                      className={`flex items-center ${getLinkClasses('/subscription')} px-2 py-1`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <CreditCardIcon className="h-4 w-4 mr-2" />
                      Subscription
                    </Link>
                    <Link 
                      to="/support" 
                      className={`${getLinkClasses('/support')} px-2 py-1`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Support
                    </Link>
                  </>
                ) : (
                  // Anonymous user mobile navigation
                  <>
                    <Link 
                      to="/jobs" 
                      className={`${getLinkClasses('/jobs')} px-2 py-1`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Jobs
                    </Link>
                    {/* <Link 
                      to="/talent" 
                      className={`${getLinkClasses('/talent')} px-2 py-1`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Talent
                    </Link> */}
                    <div className="border-t border-gray-200 my-2"></div>
                    <Link 
                      to="/login" 
                      className="text-gray-600 hover:text-gray-900 font-medium px-2 py-1"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Log In
                    </Link>
                    <Link 
                      to="/register" 
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors font-medium mx-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Workazon</h3>
              <p className="text-sm text-gray-600 mb-4">
                Connecting talented individuals with exciting career opportunities and helping employers find the perfect candidates.
              </p>
              <div className="flex space-x-4">
                <a href="mailto:support@workazon.com" className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Email</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/jobs" className="text-sm text-gray-600 hover:text-gray-900">
                    Browse Jobs
                  </Link>
                </li>
                <li>
                  <Link to="/courses" className="text-sm text-gray-600 hover:text-gray-900">
                    Courses
                  </Link>
                </li>
                <li>
                  <Link to="/talent" className="text-sm text-gray-600 hover:text-gray-900">
                    For Employers
                  </Link>
                </li>
                <li>
                  <Link to="/subscription" className="text-sm text-gray-600 hover:text-gray-900">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase mb-4">Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-gray-900">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/support" className="text-sm text-gray-600 hover:text-gray-900">
                    Help Center
                  </Link>
                </li>
                <li>
                  <a href="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">
                    Documentation
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-gray-600 hover:text-gray-900">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-gray-600 hover:text-gray-900">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              © {new Date().getFullYear()} Workazon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 